import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { IS_APP_BUILD } from '@/lib/appBrand';

/**
 * Video-room REDESIGN preview (`/app/live-preview`). A fully mocked, LiveKit-free
 * page for iterating on the live-lesson UI: brand-styled tiles, role-based control
 * bar, a right drawer (chat / participants) that PUSHES the stage (never overlays)
 * and shrinks it proportionally, grid ⇄ speaker layouts, reactions, raise-hand,
 * roll-call, record / captions / speaking-timer, breakout rooms. Fake participants
 * so we can look at the design without a real call. Everything rounded, dark cool
 * stage with our orange (#FF941F) accents.
 */

type Conn = 'good' | 'ok' | 'bad';
type Reaction = { id: number; emoji: string; left: number };

type P = {
  id: string;
  name: string;
  initials: string;
  isHost: boolean;
  micOn: boolean;
  camOn: boolean;
  speaking: boolean;
  handRaised: boolean;
  conn: Conn;
  grad: string; // tailwind gradient for the fake camera feed
};

// Reactions: Bil sticker where the pose reads clearly, plain emoji where Bil is
// ambiguous (clap/wow). `key` travels on the wire.
type ReactionDef = { key: string; src?: string; emoji?: string };
// ⚠️ В store-сборке стикеров лисы нет (D76, 25.09.2026): там папки mascot/ нет — те же реакции эмодзи.
const REACTIONS: ReactionDef[] = IS_APP_BUILD
  ? [
      { key: 'like', emoji: '👍' },
      { key: 'love', emoji: '❤️' },
      { key: 'laugh', emoji: '😂' },
      { key: 'party', emoji: '🎉' },
      { key: 'clap', emoji: '👏' },
      { key: 'wow', emoji: '😮' },
    ]
  : [
      { key: 'like', src: '/mascot/bil_react_like.webp?v=3' },
      { key: 'love', src: '/mascot/bil_react_love.webp?v=3' },
      { key: 'laugh', src: '/mascot/bil_react_laugh.webp?v=3' },
      { key: 'party', src: '/mascot/bil_react_party.webp?v=3' },
      { key: 'clap', emoji: '👏' },
      { key: 'wow', emoji: '😮' },
    ];
const REACTION_MAP: Record<string, ReactionDef> = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));

const PARTICIPANTS: P[] = [
  { id: 'host', name: 'Miss Dana', initials: 'MD', isHost: true, micOn: true, camOn: true, speaking: true, handRaised: false, conn: 'good', grad: 'from-[#3a2a1a] to-[#1b1410]' },
  { id: 'you', name: 'Вы', initials: 'ВЫ', isHost: false, micOn: true, camOn: true, speaking: false, handRaised: false, conn: 'good', grad: 'from-[#1a2436] to-[#111826]' },
  { id: 'a', name: 'Aruzhan K.', initials: 'AK', isHost: false, micOn: false, camOn: true, speaking: false, handRaised: true, conn: 'ok', grad: 'from-[#2a1a2e] to-[#181019]' },
  { id: 'b', name: 'Bekzat S.', initials: 'BS', isHost: false, micOn: true, camOn: false, speaking: false, handRaised: false, conn: 'good', grad: 'from-[#16302a] to-[#0f1c18]' },
  { id: 'c', name: 'Dana T.', initials: 'DT', isHost: false, micOn: true, camOn: true, speaking: false, handRaised: false, conn: 'bad', grad: 'from-[#302016] to-[#1a120c]' },
  { id: 'd', name: 'Erlan M.', initials: 'EM', isHost: false, micOn: false, camOn: true, speaking: false, handRaised: false, conn: 'good', grad: 'from-[#1c2440] to-[#121728]' },
  { id: 'e', name: 'Fatima N.', initials: 'FN', isHost: false, micOn: true, camOn: true, speaking: false, handRaised: true, conn: 'ok', grad: 'from-[#301826] to-[#1a0f16]' },
];

export function VideoRoomPreviewPage() {
  const { t } = useTranslation();
  const [role, setRole] = useState<'host' | 'student'>('host');
  const [drawer, setDrawer] = useState<null | 'chat' | 'people'>('people');
  const [layout, setLayout] = useState<'grid' | 'speaker'>('grid');
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [hand, setHand] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [captions, setCaptions] = useState(false);
  const [pinned, setPinned] = useState<string>('host');
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [speakTimer, setSpeakTimer] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [breakoutOpen, setBreakoutOpen] = useState(false);
  const rid = useRef(0);

  // Elapsed lesson time (mock, ticks up from 0).
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Speaking-task countdown (host tool, e.g. IELTS Part 2 = 1 min).
  useEffect(() => {
    if (speakTimer == null) return;
    if (speakTimer <= 0) {
      setSpeakTimer(null);
      return;
    }
    const id = window.setTimeout(() => setSpeakTimer((s) => (s == null ? null : s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [speakTimer]);

  const isHost = role === 'host';

  const people = useMemo(
    () => PARTICIPANTS.map((p) => (p.id === 'you' ? { ...p, micOn: mic, camOn: cam, handRaised: hand } : p)),
    [mic, cam, hand],
  );
  const raisedHands = people.filter((p) => p.handRaised);

  const fireReaction = (emoji: string) => {
    const id = (rid.current += 1);
    setReactions((r) => [...r, { id, emoji, left: 10 + Math.random() * 70 }]);
    window.setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 2200);
    setMoreOpen(false);
  };

  const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const stage = layout === 'grid' ? people : people;
  const pinnedP = people.find((p) => p.id === pinned) ?? people[0];
  const others = people.filter((p) => p.id !== pinnedP.id);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0e121b] text-white">
      <style>{`@keyframes vrp-float{0%{transform:translateY(0) scale(.6);opacity:0}15%{opacity:1;transform:translateY(-8px) scale(1)}100%{transform:translateY(-160px) scale(1.1);opacity:0}}`}</style>

      {/* ── Top bar: title · timer · rec · preview role switch ── */}
      <header className="flex items-center gap-3 px-3 py-2.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-primary font-display text-sm font-extrabold text-on-primary">O</span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">Week 1 · IELTS Listening</p>
            <p className="flex items-center gap-2 text-[11px] text-white/50">
              <span className="material-symbols-outlined text-[13px]">schedule</span>
              {mmss(elapsed)}
              {recording && (
                <span className="flex items-center gap-1 rounded-pill bg-error/20 px-1.5 py-0.5 font-semibold text-error">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-error" /> REC
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* preview-only role switch */}
          <div className="flex items-center gap-1 rounded-pill bg-white/10 p-1 text-[11px] font-semibold">
            {(['host', 'student'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={cn('rounded-pill px-2.5 py-1 transition', role === r ? 'bg-primary text-on-primary' : 'text-white/60 hover:text-white')}
              >
                {r === 'host' ? t('videoRoom.host') : t('videoRoom.student')}
              </button>
            ))}
          </div>
          <LayoutToggle layout={layout} setLayout={setLayout} />
        </div>
      </header>

      {/* ── Body: stage (shrinks when drawer open) + right drawer ── */}
      <div className="flex min-h-0 flex-1 gap-2 px-2 pb-2 sm:gap-3 sm:px-3">
        {/* Stage */}
        <div className="relative min-w-0 flex-1 rounded-card">
          {sharing ? (
            <ScreenShareView pinned={pinnedP} others={others} />
          ) : layout === 'grid' ? (
            <div className={cn('grid h-full gap-2 sm:gap-3', gridCols(stage.length))}>
              {stage.map((p) => (
                <Tile key={p.id} p={p} isHostView={isHost} onPin={() => { setPinned(p.id); setLayout('speaker'); }} />
              ))}
            </div>
          ) : (
            <SpeakerView pinned={pinnedP} others={others} isHost={isHost} onPin={setPinned} />
          )}

          {/* Self PiP (small) */}
          <SelfPip mic={mic} cam={cam} />

          {/* Floating reactions */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-card">
            {reactions.map((r) => {
              const rc = REACTION_MAP[r.emoji];
              return (
                <span key={r.id} style={{ left: `${r.left}%`, bottom: '8%', animation: 'vrp-float 2.2s ease-out forwards' }} className="absolute">
                  {rc?.src ? (
                    <img src={rc.src} alt="" className="h-16 w-auto drop-shadow-lg" />
                  ) : (
                    <span className="text-4xl">{rc?.emoji ?? r.emoji}</span>
                  )}
                </span>
              );
            })}
          </div>

          {/* Live captions bar */}
          {captions && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 max-w-[80%] -translate-x-1/2 rounded-card bg-black/70 px-4 py-2 text-center text-sm backdrop-blur">
              <span className="text-white/60">Miss Dana: </span>Let&apos;s look at the first listening task together…
            </div>
          )}

          {/* Speaking-task countdown chip */}
          {speakTimer != null && (
            <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-pill bg-primary px-3 py-1.5 text-sm font-bold text-on-primary shadow-ambient">
              <span className="material-symbols-outlined text-base">timer</span>
              {mmss(speakTimer)}
            </div>
          )}
        </div>

        {/* Right drawer — pushes the stage, rounded, tabbed */}
        {drawer && (
          <aside className="hidden w-80 shrink-0 flex-col overflow-hidden rounded-card bg-surface-container-lowest text-on-surface sm:flex">
            <DrawerHeader drawer={drawer} setDrawer={setDrawer} onClose={() => setDrawer(null)} />
            <div className="min-h-0 flex-1 overflow-y-auto">
              {drawer === 'chat' ? <ChatPanel /> : <PeoplePanel people={people} isHost={isHost} raised={raisedHands} />}
            </div>
          </aside>
        )}
      </div>

      {/* ── Bottom control bar (role-based) ── */}
      <footer className="px-2 pb-3 sm:px-3">
        <div className="mx-auto flex max-w-4xl items-center justify-center gap-1.5 rounded-dock bg-white/10 px-2 py-2 backdrop-blur sm:gap-2 sm:px-3">
          {/* shared core */}
          <Ctrl icon={mic ? 'mic' : 'mic_off'} label={t('videoRoom.mic')} active={mic} danger={!mic} onClick={() => setMic((v) => !v)} />
          {/* Кнопка камеры — только сайт (02.10.2026): в приложении урок без камеры, и макет не должен её обещать. */}
          {IS_APP_BUILD ? null : (
            <Ctrl icon={cam ? 'videocam' : 'videocam_off'} label={t('videoRoom.camera')} active={cam} danger={!cam} onClick={() => setCam((v) => !v)} />
          )}

          {/* host-only */}
          {isHost && <Ctrl icon="screen_share" label={t('videoRoom.share')} active={sharing} onClick={() => setSharing((v) => !v)} />}
          {isHost && <Ctrl icon="radio_button_checked" label={t('videoRoom.record')} active={recording} danger={recording} onClick={() => setRecording((v) => !v)} />}
          {isHost && <Ctrl icon="how_to_reg" label={t('videoRoom.rollCall')} onClick={() => {}} />}

          {/* student-only */}
          {!isHost && <Ctrl icon="front_hand" label={t('videoRoom.raiseHand')} active={hand} onClick={() => setHand((v) => !v)} />}

          {/* shared */}
          <Ctrl icon="mood" label={t('videoRoom.reactions')} onClick={() => setMoreOpen((v) => !v)} badge={reactions.length ? undefined : undefined} />
          <Ctrl icon="chat" label={t('videoRoom.chat')} active={drawer === 'chat'} onClick={() => setDrawer((d) => (d === 'chat' ? null : 'chat'))} />
          <Ctrl icon="group" label={t('videoRoom.people')} active={drawer === 'people'} onClick={() => setDrawer((d) => (d === 'people' ? null : 'people'))} badge={raisedHands.length || undefined} />

          {/* overflow (settings / fullscreen / captions / breakout / mute-all / speaking-timer) */}
          <div className="relative">
            <Ctrl icon="more_horiz" label={t('videoRoom.more')} active={moreOpen} onClick={() => setMoreOpen((v) => !v)} />
            {moreOpen && (
              <MoreMenu
                isHost={isHost}
                captions={captions}
                onCaptions={() => { setCaptions((v) => !v); setMoreOpen(false); }}
                onFullscreen={() => setMoreOpen(false)}
                onSettings={() => setMoreOpen(false)}
                onMuteAll={() => setMoreOpen(false)}
                onBreakout={() => { setBreakoutOpen(true); setMoreOpen(false); }}
                onSpeakTimer={() => { setSpeakTimer(60); setMoreOpen(false); }}
                reactions={REACTIONS}
                onReact={fireReaction}
              />
            )}
          </div>

          {/* leave / end — always last, red */}
          <button
            onClick={() => {}}
            className="ml-1 flex items-center gap-1.5 rounded-pill bg-error px-3.5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110"
          >
            <span className="material-symbols-outlined text-xl">call_end</span>
            <span className="hidden md:inline">{isHost ? t('videoRoom.endForAll') : t('videoRoom.leave')}</span>
          </button>
        </div>
      </footer>

      {breakoutOpen && <BreakoutModal onClose={() => setBreakoutOpen(false)} people={people} />}
    </div>
  );
}

// ── Tiles ──────────────────────────────────────────────────────────

function Tile({ p, isHostView, onPin }: { p: P; isHostView: boolean; onPin: () => void }) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-card bg-gradient-to-br ring-1 ring-white/10 transition',
        p.grad,
        p.speaking && 'ring-2 ring-primary',
      )}
    >
      {/* fake feed / avatar */}
      <div className="grid h-full min-h-[96px] place-items-center">
        {p.camOn ? (
          <span className="material-symbols-outlined text-4xl text-white/15">person</span>
        ) : (
          <span className="grid h-14 w-14 place-items-center rounded-full bg-white/10 font-display text-lg font-bold text-white/80">
            {p.initials}
          </span>
        )}
      </div>

      {/* top-right badges */}
      <div className="absolute right-2 top-2 flex items-center gap-1">
        {p.handRaised && (
          <span className="grid h-6 w-6 place-items-center rounded-full bg-warning text-black">
            <span className="material-symbols-outlined text-sm">front_hand</span>
          </span>
        )}
        <ConnDots conn={p.conn} />
      </div>

      {/* hover: pin/spotlight + host controls */}
      <div className="absolute right-2 top-2 hidden gap-1 group-hover:flex">
        <IconBtn icon="push_pin" onClick={onPin} title="Pin" />
        {isHostView && !p.isHost && <IconBtn icon="mic_off" onClick={() => {}} title="Mute" />}
        {isHostView && !p.isHost && <IconBtn icon="person_remove" onClick={() => {}} title="Remove" danger />}
      </div>

      {/* bottom: name + mic */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-1.5">
        <span
          className={cn(
            'material-symbols-outlined text-base',
            p.micOn ? 'text-white/80' : 'text-error',
          )}
        >
          {p.micOn ? 'mic' : 'mic_off'}
        </span>
        <span className="truncate text-xs font-semibold">{p.name}</span>
        {p.isHost && <span className="rounded-pill bg-primary/90 px-1.5 py-0.5 text-[9px] font-bold uppercase text-on-primary">host</span>}
      </div>
    </div>
  );
}

function SpeakerView({ pinned, others, isHost, onPin }: { pinned: P; others: P[]; isHost: boolean; onPin: (id: string) => void }) {
  return (
    <div className="flex h-full flex-col gap-2 sm:gap-3">
      <div className="min-h-0 flex-1">
        <Tile p={pinned} isHostView={isHost} onPin={() => {}} />
      </div>
      <div className="flex h-24 shrink-0 gap-2 overflow-x-auto sm:h-28">
        {others.map((p) => (
          <button key={p.id} onClick={() => onPin(p.id)} className="aspect-video h-full shrink-0">
            <Tile p={p} isHostView={isHost} onPin={() => onPin(p.id)} />
          </button>
        ))}
      </div>
    </div>
  );
}

function ScreenShareView({ pinned, others }: { pinned: P; others: P[] }) {
  return (
    <div className="flex h-full flex-col gap-2 sm:gap-3">
      <div className="grid min-h-0 flex-1 place-items-center rounded-card bg-[#0a0d14] ring-1 ring-white/10">
        <div className="text-center text-white/40">
          <span className="material-symbols-outlined text-5xl">present_to_all</span>
          <p className="mt-2 text-sm">{pinned.name} демонстрирует экран</p>
        </div>
      </div>
      <div className="flex h-24 shrink-0 gap-2 overflow-x-auto sm:h-28">
        {[pinned, ...others].slice(0, 6).map((p) => (
          <div key={p.id} className="aspect-video h-full shrink-0">
            <Tile p={p} isHostView={false} onPin={() => {}} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SelfPip({ mic, cam }: { mic: boolean; cam: boolean }) {
  return (
    <div className="absolute bottom-3 right-3 hidden w-40 overflow-hidden rounded-card bg-gradient-to-br from-[#1a2436] to-[#111826] ring-1 ring-white/15 md:block">
      <div className="grid aspect-video place-items-center">
        {cam ? (
          <span className="material-symbols-outlined text-2xl text-white/20">person</span>
        ) : (
          <span className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-sm font-bold">ВЫ</span>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 px-2 py-1 text-[10px]">
        <span className={cn('material-symbols-outlined text-sm', mic ? 'text-white/70' : 'text-error')}>{mic ? 'mic' : 'mic_off'}</span>
        <span className="font-semibold">Вы</span>
      </div>
    </div>
  );
}

function IconBtn({ icon, onClick, title, danger }: { icon: string; onClick: () => void; title: string; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={cn(
        'grid h-7 w-7 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70',
        danger && 'hover:bg-error',
      )}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
    </button>
  );
}

function ConnDots({ conn }: { conn: Conn }) {
  const bars = conn === 'good' ? 3 : conn === 'ok' ? 2 : 1;
  const color = conn === 'good' ? 'bg-success' : conn === 'ok' ? 'bg-warning' : 'bg-error';
  return (
    <span className="flex h-6 items-end gap-0.5 rounded-full bg-black/40 px-1.5 py-1">
      {[1, 2, 3].map((n) => (
        <span key={n} className={cn('w-1 rounded-full', n <= bars ? color : 'bg-white/25')} style={{ height: `${4 + n * 3}px` }} />
      ))}
    </span>
  );
}

// ── Controls ───────────────────────────────────────────────────────

function Ctrl({
  icon,
  label,
  active,
  danger,
  badge,
  onClick,
}: {
  icon: string;
  label: string;
  active?: boolean;
  danger?: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'relative grid h-11 w-11 place-items-center rounded-full transition sm:h-12 sm:w-12',
        danger
          ? 'bg-error text-white hover:brightness-110'
          : active
            ? 'bg-primary text-on-primary'
            : 'bg-white/10 text-white hover:bg-white/20',
      )}
    >
      <span className="material-symbols-outlined text-xl">{icon}</span>
      {badge ? (
        <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-warning px-1 text-[10px] font-bold text-black">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function LayoutToggle({ layout, setLayout }: { layout: 'grid' | 'speaker'; setLayout: (l: 'grid' | 'speaker') => void }) {
  return (
    <div className="flex items-center gap-1 rounded-pill bg-white/10 p-1">
      {(['grid', 'speaker'] as const).map((l) => (
        <button
          key={l}
          onClick={() => setLayout(l)}
          title={l}
          className={cn('grid h-8 w-8 place-items-center rounded-full transition', layout === l ? 'bg-primary text-on-primary' : 'text-white/60 hover:text-white')}
        >
          <span className="material-symbols-outlined text-lg">{l === 'grid' ? 'grid_view' : 'co_present'}</span>
        </button>
      ))}
    </div>
  );
}

function MoreMenu({
  isHost,
  captions,
  onCaptions,
  onFullscreen,
  onSettings,
  onMuteAll,
  onBreakout,
  onSpeakTimer,
  reactions,
  onReact,
}: {
  isHost: boolean;
  captions: boolean;
  onCaptions: () => void;
  onFullscreen: () => void;
  onSettings: () => void;
  onMuteAll: () => void;
  onBreakout: () => void;
  onSpeakTimer: () => void;
  reactions: ReactionDef[];
  onReact: (e: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="absolute bottom-14 left-1/2 w-60 -translate-x-1/2 rounded-card bg-surface-container-lowest p-2 text-on-surface shadow-ambient">
      {/* quick reactions row */}
      <div className="mb-1.5 flex items-center justify-between gap-1 rounded-button bg-surface-container-low px-2 py-1.5">
        {reactions.map((r) => (
          <button key={r.key} onClick={() => onReact(r.key)} className="grid h-9 w-9 place-items-center rounded-full transition hover:scale-125" title={r.key}>
            {r.src ? (
              <img src={r.src} alt={r.key} className="h-8 w-8 object-contain" />
            ) : (
              <span className="text-2xl leading-none">{r.emoji}</span>
            )}
          </button>
        ))}
      </div>
      <MenuItem icon="closed_caption" label={t('videoRoom.captions')} active={captions} onClick={onCaptions} />
      <MenuItem icon="settings" label={t('videoRoom.settings')} onClick={onSettings} />
      <MenuItem icon="fullscreen" label={t('videoRoom.fullscreen')} onClick={onFullscreen} />
      {isHost && <div className="my-1 border-t border-outline-variant/20" />}
      {isHost && <MenuItem icon="timer" label={t('videoRoom.speakingTimer')} onClick={onSpeakTimer} />}
      {isHost && <MenuItem icon="groups" label={t('videoRoom.breakout')} onClick={onBreakout} />}
      {isHost && <MenuItem icon="volume_off" label={t('videoRoom.muteAll')} onClick={onMuteAll} />}
      {isHost && <MenuItem icon="poll" label={t('videoRoom.poll')} onClick={() => {}} />}
    </div>
  );
}

function MenuItem({ icon, label, active, onClick }: { icon: string; label: string; active?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-button px-2.5 py-2 text-sm font-medium transition hover:bg-surface-container-low',
        active ? 'text-primary' : 'text-on-surface',
      )}
    >
      <span className="material-symbols-outlined text-xl">{icon}</span>
      {label}
    </button>
  );
}

// ── Drawer panels ──────────────────────────────────────────────────

function DrawerHeader({ drawer, setDrawer, onClose }: { drawer: 'chat' | 'people'; setDrawer: (d: 'chat' | 'people') => void; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 border-b border-outline-variant/20 p-2">
      <div className="flex flex-1 gap-1">
        {(['chat', 'people'] as const).map((d) => (
          <button
            key={d}
            onClick={() => setDrawer(d)}
            className={cn(
              'flex-1 rounded-button px-3 py-2 text-sm font-semibold transition',
              drawer === d ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-variant hover:bg-surface-container-low',
            )}
          >
            {d === 'chat' ? t('videoRoom.chat') : t('videoRoom.people')}
          </button>
        ))}
      </div>
      <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-button text-on-surface-variant hover:bg-surface-container-low">
        <span className="material-symbols-outlined">close</span>
      </button>
    </div>
  );
}

function ChatPanel() {
  const { t } = useTranslation();
  const msgs = [
    { who: 'Miss Dana', me: false, text: 'Welcome everyone! We start with Part 1.' },
    { who: 'Aruzhan K.', me: false, text: 'Ready! 🙌' },
    { who: 'Вы', me: true, text: 'Можно повторить задание 3?' },
    { who: 'Miss Dana', me: false, text: 'Sure — look at the shared screen.' },
  ];
  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 p-3">
        {msgs.map((m, i) => (
          <div key={i} className={cn('flex flex-col', m.me && 'items-end')}>
            {!m.me && <span className="mb-0.5 text-[11px] font-semibold text-on-surface-variant">{m.who}</span>}
            <span
              className={cn(
                'max-w-[85%] rounded-card px-3 py-2 text-sm',
                m.me ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface',
              )}
            >
              {m.text}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 border-t border-outline-variant/20 p-2">
        <button className="grid h-9 w-9 shrink-0 place-items-center rounded-button text-on-surface-variant hover:bg-surface-container-low">
          <span className="material-symbols-outlined">attach_file</span>
        </button>
        <input
          placeholder={t('videoRoom.messagePlaceholder')}
          className="min-w-0 flex-1 rounded-button bg-surface-container-low px-3 py-2 text-sm text-on-surface outline-none ring-1 ring-inset ring-outline-variant/20 focus:ring-2 focus:ring-primary"
        />
        <button className="grid h-9 w-9 shrink-0 place-items-center rounded-button bg-primary text-on-primary">
          <span className="material-symbols-outlined">send</span>
        </button>
      </div>
    </div>
  );
}

function PeoplePanel({ people, isHost, raised }: { people: P[]; isHost: boolean; raised: P[] }) {
  const { t } = useTranslation();
  return (
    <div className="p-3">
      {raised.length > 0 && (
        <div className="mb-3 rounded-card bg-warning-container/40 p-2">
          <p className="mb-1.5 flex items-center gap-1 px-1 text-[11px] font-semibold uppercase tracking-label text-on-surface-variant">
            <span className="material-symbols-outlined text-sm">front_hand</span>
            {t('videoRoom.raisedQueue', { count: raised.length })}
          </p>
          {raised.map((p) => (
            <Row key={p.id} p={p} isHost={isHost} highlight />
          ))}
        </div>
      )}
      <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-label text-on-surface-variant">
        {t('videoRoom.inRoom', { count: people.length })}
      </p>
      {people.map((p) => (
        <Row key={p.id} p={p} isHost={isHost} />
      ))}
    </div>
  );
}

function Row({ p, isHost, highlight }: { p: P; isHost: boolean; highlight?: boolean }) {
  return (
    <div className={cn('flex items-center gap-2.5 rounded-button px-2 py-1.5', highlight ? '' : 'hover:bg-surface-container-low')}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-container text-xs font-bold text-on-primary-container">
        {p.initials}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-on-surface">
        {p.name}
        {p.isHost && <span className="ml-1.5 rounded-pill bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary">host</span>}
      </span>
      {p.handRaised && <span className="material-symbols-outlined text-base text-warning">front_hand</span>}
      <span className={cn('material-symbols-outlined text-base', p.micOn ? 'text-on-surface-variant' : 'text-error')}>{p.micOn ? 'mic' : 'mic_off'}</span>
      {isHost && !p.isHost && (
        <button className="grid h-7 w-7 place-items-center rounded-button text-on-surface-variant hover:bg-surface-container">
          <span className="material-symbols-outlined text-base">more_vert</span>
        </button>
      )}
    </div>
  );
}

function BreakoutModal({ onClose, people }: { onClose: () => void; people: P[] }) {
  const { t } = useTranslation();
  const students = people.filter((p) => !p.isHost);
  const rooms = [students.slice(0, 2), students.slice(2, 4), students.slice(4)];
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-card bg-surface-container-lowest p-4 text-on-surface" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">groups</span>
          <h3 className="font-display text-lg font-bold">{t('videoRoom.breakout')}</h3>
          <button onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-button hover:bg-surface-container-low">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <p className="mb-3 text-sm text-on-surface-variant">{t('videoRoom.breakoutHint')}</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {rooms.map((r, i) => (
            <div key={i} className="rounded-card bg-surface-container-low p-2.5">
              <p className="mb-2 text-xs font-bold uppercase tracking-label text-primary">Room {i + 1}</p>
              <div className="space-y-1.5">
                {r.map((p) => (
                  <div key={p.id} className="flex items-center gap-1.5 text-sm">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-primary-container text-[10px] font-bold text-on-primary-container">{p.initials}</span>
                    <span className="truncate">{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button onClick={onClose} className="mt-4 w-full rounded-button bg-primary py-2.5 text-sm font-semibold text-on-primary">
          {t('videoRoom.startBreakout')}
        </button>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function gridCols(n: number): string {
  if (n <= 1) return 'grid-cols-1';
  if (n <= 2) return 'grid-cols-1 sm:grid-cols-2';
  if (n <= 4) return 'grid-cols-2';
  if (n <= 6) return 'grid-cols-2 sm:grid-cols-3';
  return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
}
